<template>
  <div class="privacy-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            Privacy Policy
          </h1>
          <p class="hero-subtitle">
            Your Privacy Matters to Us
          </p>
          <div class="hero-badge">
            <i class="fas fa-shield-alt mr-2" />
            Privacy Policy
          </div>
        </div>
      </div>
    </section>

    <!-- Table of Contents -->
    <section class="toc-section">
      <div class="container">
        <div class="toc-card">
          <h2 class="toc-title">
            Table of Contents
          </h2>
          <div class="toc-grid">
            <a
              v-for="(section, index) in tableOfContents"
              :key="index"
              :href="`#section-${index + 1}`"
              class="toc-item"
              @click="scrollToSection(index + 1)"
            >
              <span class="toc-number">{{ index + 1 }}</span>
              <span class="toc-text">{{ section }}</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Privacy Content -->
    <section class="privacy-content">
      <div class="container">
        <div class="content-wrapper">
          <!-- Section 1: Information We Collect -->
          <div
            id="section-1"
            class="privacy-section"
          >
            <h2 class="section-title">
              1. Information We Collect
            </h2>
            <div class="privacy-content-text">
              <p><strong>Personal Information:</strong> When you create an account with Indiefy, we collect personal information such as your name, email address, phone number, and billing information.</p>

              <p><strong>Music Content:</strong> We collect and store the music files, artwork, metadata, and other content you upload to our platform for distribution purposes.</p>

              <p><strong>Usage Data:</strong> We automatically collect information about how you use our service, including your IP address, browser type, device information, and pages visited.</p>

              <p><strong>Payment Information:</strong> We collect payment and billing information when you make purchases or receive royalty payments through our platform.</p>

              <p><strong>Communications:</strong> We may collect information from your communications with us, including customer support interactions and feedback.</p>
            </div>
          </div>

          <!-- Section 2: How We Use Your Information -->
          <div
            id="section-2"
            class="privacy-section"
          >
            <h2 class="section-title">
              2. How We Use Your Information
            </h2>
            <div class="privacy-content-text">
              <div class="usage-list">
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Service Provision:</strong> To provide, maintain, and improve our music distribution services.</p>
                </div>
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Account Management:</strong> To create and manage your account, process payments, and handle royalty distributions.</p>
                </div>
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Communication:</strong> To send you important updates, notifications, and respond to your inquiries.</p>
                </div>
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Analytics:</strong> To analyze usage patterns and improve our platform performance and user experience.</p>
                </div>
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights and the rights of our users.</p>
                </div>
                <div class="usage-item">
                  <span class="usage-letter">•</span>
                  <p><strong>Marketing:</strong> To send you promotional materials and updates about our services (with your consent).</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: Information Sharing -->
          <div
            id="section-3"
            class="privacy-section"
          >
            <h2 class="section-title">
              3. Information Sharing
            </h2>
            <div class="privacy-content-text">
              <p>We may share your information in the following circumstances:</p>

              <div class="sharing-list">
                <div class="sharing-item">
                  <h4>Digital Service Providers (DSPs)</h4>
                  <p>We share your music content and metadata with streaming platforms and digital stores for distribution purposes.</p>
                </div>

                <div class="sharing-item">
                  <h4>Service Providers</h4>
                  <p>We work with third-party service providers who help us operate our platform, process payments, and provide customer support.</p>
                </div>

                <div class="sharing-item">
                  <h4>Legal Requirements</h4>
                  <p>We may disclose information when required by law, court order, or to protect our rights and safety.</p>
                </div>

                <div class="sharing-item">
                  <h4>Business Transfers</h4>
                  <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Data Security -->
          <div
            id="section-4"
            class="privacy-section"
          >
            <h2 class="section-title">
              4. Data Security
            </h2>
            <div class="privacy-content-text">
              <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

              <div class="security-measures">
                <div class="security-item">
                  <i class="fas fa-lock" />
                  <h4>Encryption</h4>
                  <p>We use SSL/TLS encryption to protect data in transit and encrypt sensitive data at rest.</p>
                </div>

                <div class="security-item">
                  <i class="fas fa-server" />
                  <h4>Secure Infrastructure</h4>
                  <p>Our servers are hosted in secure facilities with restricted access and regular security monitoring.</p>
                </div>

                <div class="security-item">
                  <i class="fas fa-users-cog" />
                  <h4>Access Controls</h4>
                  <p>We limit access to personal information to authorized personnel who need it for business purposes.</p>
                </div>

                <div class="security-item">
                  <i class="fas fa-sync-alt" />
                  <h4>Regular Updates</h4>
                  <p>We regularly update our security measures and conduct security assessments.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 5: Your Rights -->
          <div
            id="section-5"
            class="privacy-section"
          >
            <h2 class="section-title">
              5. Your Rights and Choices
            </h2>
            <div class="privacy-content-text">
              <p>You have the following rights regarding your personal information:</p>

              <div class="rights-list">
                <div class="rights-item">
                  <span class="rights-icon">📄</span>
                  <div class="rights-content">
                    <h4>Access</h4>
                    <p>You can request a copy of the personal information we hold about you.</p>
                  </div>
                </div>

                <div class="rights-item">
                  <span class="rights-icon">✏️</span>
                  <div class="rights-content">
                    <h4>Correction</h4>
                    <p>You can request that we correct inaccurate or incomplete personal information.</p>
                  </div>
                </div>

                <div class="rights-item">
                  <span class="rights-icon">🗑️</span>
                  <div class="rights-content">
                    <h4>Deletion</h4>
                    <p>You can request that we delete your personal information, subject to certain limitations.</p>
                  </div>
                </div>

                <div class="rights-item">
                  <span class="rights-icon">📧</span>
                  <div class="rights-content">
                    <h4>Opt-out</h4>
                    <p>You can opt-out of marketing communications at any time.</p>
                  </div>
                </div>

                <div class="rights-item">
                  <span class="rights-icon">📋</span>
                  <div class="rights-content">
                    <h4>Data Portability</h4>
                    <p>You can request a copy of your data in a structured, machine-readable format.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 6: Cookies and Tracking -->
          <div
            id="section-6"
            class="privacy-section"
          >
            <h2 class="section-title">
              6. Cookies and Tracking Technologies
            </h2>
            <div class="privacy-content-text">
              <p>We use cookies and similar tracking technologies to enhance your experience on our platform:</p>

              <div class="cookie-types">
                <div class="cookie-type">
                  <h4>Essential Cookies</h4>
                  <p>Required for the website to function properly, including login sessions and security features.</p>
                </div>

                <div class="cookie-type">
                  <h4>Analytics Cookies</h4>
                  <p>Help us understand how visitors use our website to improve performance and user experience.</p>
                </div>

                <div class="cookie-type">
                  <h4>Functional Cookies</h4>
                  <p>Remember your preferences and settings to enhance your user experience.</p>
                </div>

                <div class="cookie-type">
                  <h4>Marketing Cookies</h4>
                  <p>Used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.</p>
                </div>
              </div>

              <p>You can manage your cookie preferences through your browser settings or by contacting us.</p>
            </div>
          </div>

          <!-- Section 7: International Transfers -->
          <div
            id="section-7"
            class="privacy-section"
          >
            <h2 class="section-title">
              7. International Data Transfers
            </h2>
            <div class="privacy-content-text">
              <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers are conducted in accordance with applicable data protection laws and implement appropriate safeguards to protect your information.</p>

              <p>When we transfer personal information from the European Economic Area (EEA) to other countries, we rely on adequacy decisions by the European Commission or implement appropriate safeguards such as Standard Contractual Clauses.</p>
            </div>
          </div>

          <!-- Section 8: Data Retention -->
          <div
            id="section-8"
            class="privacy-section"
          >
            <h2 class="section-title">
              8. Data Retention
            </h2>
            <div class="privacy-content-text">
              <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>

              <div class="retention-periods">
                <div class="retention-item">
                  <h4>Account Information</h4>
                  <p>Retained for the duration of your account plus 7 years for legal and tax purposes.</p>
                </div>

                <div class="retention-item">
                  <h4>Music Content</h4>
                  <p>Retained as long as your content is distributed or as required for royalty reporting.</p>
                </div>

                <div class="retention-item">
                  <h4>Financial Records</h4>
                  <p>Retained for 7 years in accordance with tax and accounting regulations.</p>
                </div>

                <div class="retention-item">
                  <h4>Marketing Data</h4>
                  <p>Retained until you opt-out or for 3 years from your last interaction.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 9: Children's Privacy -->
          <div
            id="section-9"
            class="privacy-section"
          >
            <h2 class="section-title">
              9. Children's Privacy
            </h2>
            <div class="privacy-content-text">
              <p>Our services are not intended for children under the age of 16. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information promptly.</p>

              <p>For users between 16 and 18 years old, we may require parental consent and additional verification as outlined in our Terms and Conditions.</p>
            </div>
          </div>

          <!-- Section 10: Changes to Privacy Policy -->
          <div
            id="section-10"
            class="privacy-section"
          >
            <h2 class="section-title">
              10. Changes to This Privacy Policy
            </h2>
            <div class="privacy-content-text">
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by:</p>

              <ul class="notification-methods">
                <li>Posting the updated policy on our website</li>
                <li>Sending you an email notification</li>
                <li>Displaying a prominent notice on our platform</li>
              </ul>

              <p>Your continued use of our services after the effective date of the updated Privacy Policy constitutes your acceptance of the changes.</p>
            </div>
          </div>

          <!-- Section 11: Contact Information -->
          <div
            id="section-11"
            class="privacy-section"
          >
            <h2 class="section-title">
              11. Contact Us
            </h2>
            <div class="privacy-content-text">
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>

              <div class="contact-info">
                <div class="contact-method">
                  <i class="fas fa-envelope" />
                  <div class="contact-details">
                    <h4>Email</h4>
                    <p>support@indiefy.net</p>
                  </div>
                </div>

                <div class="contact-method">
                  <i class="fas fa-map-marker-alt" />
                  <div class="contact-details">
                    <h4>Address</h4>
                    <p>
                      Indiefy Inc.<br>
                      Jalisco, Mexico
                    </p>
                  </div>
                </div>

                <div class="contact-method">
                  <i class="fas fa-clock" />
                  <div class="contact-details">
                    <h4>Response Time</h4>
                    <p>We will respond to your inquiry within 30 days.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Last Updated Section -->
    <section class="last-updated-section">
      <div class="container">
        <div class="last-updated-card">
          <p>Last updated: <strong>{{ new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }}</strong></p>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section">
      <div class="container">
        <div class="contact-card">
          <h3>Questions About Your Privacy?</h3>
          <p>If you have any questions about this privacy policy or how we handle your personal information, please don't hesitate to contact our privacy team.</p>
          <a
            href="mailto:support@indiefy.net"
            class="contact-button"
          >
            <i class="fas fa-envelope mr-2" />
            Contact Privacy Team
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'privacy.seo-title',
  descriptionKey: 'privacy.seo-description',
});

const tableOfContents = [
  'Information We Collect',
  'How We Use Your Information',
  'Information Sharing',
  'Data Security',
  'Your Rights and Choices',
  'Cookies and Tracking Technologies',
  'International Data Transfers',
  'Data Retention',
  'Children\'s Privacy',
  'Changes to This Privacy Policy',
  'Contact Us',
];

const scrollToSection = (sectionNumber) => {
  const section = document.getElementById(`section-${sectionNumber}`);
  if (section) {
    section.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/breakpoints";

.privacy-view {
  min-height: 100vh;
  background-color: var(--color-background, #fafafa);

  .body--dark & {
    background-color: var(--color-background, #0a0a0a);
  }
}

// Hero Section
.hero-section {
  background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
  padding: 5rem 1.5rem 3rem;
  color: white;
  text-align: center;

  .hero-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    @media (max-width: $tablet) {
      font-size: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 2rem;
    }
  }

  .hero-subtitle {
    font-size: 1.5rem;
    font-weight: 400;
    margin-bottom: 2rem;
    opacity: 0.9;

    @media (max-width: $mobile) {
      font-size: 1.2rem;
    }
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    backdrop-filter: blur(10px);
  }
}

// Table of Contents
.toc-section {
  padding: 3rem 1.5rem;
  background: var(--color-background, white);

  .body--dark & {
    background: var(--color-background, #0f0f0f);
  }
}

.toc-card {
  background: var(--color-card-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  max-width: 800px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  .toc-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text, #333);
    margin-bottom: 2rem;
    text-align: center;
  }

  .toc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  .toc-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    text-decoration: none;
    color: var(--color-text, #555);
    transition: all 0.3s ease;
    border: 2px solid transparent;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    &:hover {
      background: var(--color-primary-light, #e3f2fd);
      border-color: $indiefy-blue;
      transform: translateY(-2px);
      color: $indiefy-blue;

      .body--dark & {
        background: var(--color-primary-dark, #1a237e);
      }

      .toc-number {
        background: $indiefy-blue;
        color: white;
      }
    }

    .toc-number {
      background: $indiefy-lavender;
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
      margin-right: 1rem;
      transition: all 0.3s ease;
    }

    .toc-text {
      font-weight: 600;
      font-size: 0.95rem;
    }
  }
}

// Privacy Content
.privacy-content {
  padding: 3rem 1.5rem;
  background: var(--color-background, white);

  .body--dark & {
    background: var(--color-background, #0f0f0f);
  }
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.privacy-section {
  margin-bottom: 4rem;
  background: var(--color-card-background, white);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 3rem;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .section-title {
    font-size: 2rem;
    font-weight: 700;
    color: $indiefy-blue;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid $indiefy-lavender;
  }
}

// Usage List
.usage-list {
  .usage-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 1rem;

    .usage-letter {
      color: $indiefy-blue;
      font-weight: 700;
      font-size: 1.2rem;
      margin-top: 0.25rem;
    }

    p {
      margin: 0;
      line-height: 1.6;
    }
  }
}

// Sharing List
.sharing-list {
  .sharing-item {
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border-left: 4px solid $indiefy-blue;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    h4 {
      color: $indiefy-blue;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      line-height: 1.6;
    }
  }
}

// Security Measures
.security-measures {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  .security-item {
    text-align: center;
    padding: 2rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    i {
      font-size: 2.5rem;
      color: $indiefy-blue;
      margin-bottom: 1rem;
    }

    h4 {
      color: $indiefy-blue;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      line-height: 1.6;
      font-size: 0.9rem;
    }
  }
}

// Rights List
.rights-list {
  .rights-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 2rem;
    gap: 1rem;

    .rights-icon {
      font-size: 1.5rem;
      margin-top: 0.25rem;
    }

    .rights-content {
      h4 {
        color: $indiefy-blue;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        line-height: 1.6;
      }
    }
  }
}

// Cookie Types
.cookie-types {
  .cookie-type {
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    h4 {
      color: $indiefy-blue;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      line-height: 1.6;
    }
  }
}

// Retention Periods
.retention-periods {
  .retention-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 8px;
    margin-bottom: 1rem;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    h4 {
      color: $indiefy-blue;
      font-weight: 700;
      margin: 0;
      flex: 1;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      flex: 2;
      text-align: right;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: flex-start;

      p {
        text-align: left;
        margin-top: 0.5rem;
      }
    }
  }
}

// Notification Methods
.notification-methods {
  list-style: none;
  padding: 0;

  li {
    padding: 0.5rem 0;
    padding-left: 1.5rem;
    position: relative;

    &::before {
      content: "•";
      color: $indiefy-blue;
      font-weight: bold;
      position: absolute;
      left: 0;
    }
  }
}

// Contact Info
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  .contact-method {
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    i {
      font-size: 1.5rem;
      color: $indiefy-blue;
      margin-top: 0.25rem;
    }

    .contact-details {
      h4 {
        color: $indiefy-blue;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        line-height: 1.6;
      }
    }
  }
}

// Last Updated Section
.last-updated-section {
  padding: 2rem 1.5rem;
  background: var(--color-background-alt, #f8f9fa);

  .body--dark & {
    background: var(--color-background-alt, #1a1a1a);
  }
}

.last-updated-card {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
  background: var(--color-card-background, white);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  p {
    margin: 0;
    font-size: 1.1rem;
    color: var(--color-text, #555);
  }
}

// Contact Section
.contact-section {
  padding: 3rem 1.5rem;
  background: var(--color-background-alt, #f8f9fa);

  .body--dark & {
    background: var(--color-background-alt, #1a1a1a);
  }
}

.contact-card {
  background: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  h3 {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text, #333);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: var(--color-text-light, #666);
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .contact-button {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
    color: white;
    padding: 1rem 2rem;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba($indiefy-blue, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba($indiefy-blue, 0.4);
      color: white;
    }
  }
}

// General Text Styling
.privacy-content-text {
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    color: var(--color-text, #555);
  }

  strong {
    color: var(--color-text, #333);
  }
}
</style>